import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { Button, Callout, ConfirmButton, SectionCard, useToast } from '@/components/ui';
import { createAppClient, normalizeApiErrorDetails, type RegistryApp } from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { AppCredentialReveal, type RevealedField } from './AppCredentialReveal';
import { useCopyToClipboard } from './useCopyToClipboard';
import { ValueRow } from './ValueRow';
import detail from './AppDetail.module.css';
import styles from './AppDetailConnectionTab.module.css';

/** `EXT_PROVISIONING_FAILED` — a misconfigured realm, not a bad request. */
const PROVISIONING_FAILED_CODE = 6006;

function provisioningDetail(error: unknown): string | null {
    if (!(error instanceof OrgAdminApiError) || error.code !== PROVISIONING_FAILED_CODE)
        return null;
    return normalizeApiErrorDetails(error.details).keycloak?.[0] ?? null;
}

interface Revealed {
    fields: RevealedField[];
    warning: string;
    rotated: boolean;
}

export interface AppDetailClientsCardProps {
    app: RegistryApp;
    issuer: string;
    /** `console.apps.credentials` — what `POST …/client` requires server-side. */
    canIssue: boolean;
}

/**
 * EAA-1709: the app's two Keycloak clients on "Kết nối kỹ thuật".
 *
 *   - LOGIN client (`kc_client_id`, web/hybrid) — public + PKCE, so there is no
 *     secret to show or re-issue for it.
 *   - MACHINE client (`kc_api_client_id` = `<code>-api`, api/hybrid) — the app
 *     backend's confidential client. "Cấp lại chuỗi bí mật" (`POST …/client`) is
 *     bound to it, and to nothing else: a web app has no such action at all.
 *
 * The secret is revealed once through `AppCredentialReveal`, in local state only.
 */
export function AppDetailClientsCard({ app, issuer, canIssue }: AppDetailClientsCardProps) {
    const { t } = useTranslation();
    const { apiBase } = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const copy = useCopyToClipboard();
    const [revealed, setRevealed] = useState<Revealed | null>(null);

    const hasLoginClient = app.app_type !== 'api';
    const hasMachineClient = app.app_type !== 'web';

    const clientMutation = useMutation({
        mutationFn: () => createAppClient(apiBase, accessToken as string, app.code),
        onSuccess: (data) => {
            setRevealed({
                fields: [
                    {
                        label: t('console.appDetail.connection.apiClient.clientId'),
                        value: data.client_id,
                    },
                    {
                        label: t('console.appDetail.connection.clientSecret'),
                        value: data.client_secret,
                        wrap: true,
                    },
                ],
                warning: data.warning,
                rotated: data.rotated,
            });
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            void queryClient.invalidateQueries({ queryKey: ['app-checklist', app.code] });
        },
        onError: (error) =>
            toast.error(
                provisioningDetail(error) ?? t('console.appDetail.connection.client.error')
            ),
    });

    let issueAction = null;
    if (canIssue && app.kc_api_client_id) {
        issueAction = (
            <ConfirmButton
                danger
                label={
                    clientMutation.isPending
                        ? t('console.appDetail.connection.client.rotating')
                        : t('console.appDetail.connection.client.rotateButton')
                }
                disabled={clientMutation.isPending}
                confirmTitle={t('console.appDetail.connection.client.rotateConfirmTitle')}
                confirmDescription={t('console.appDetail.connection.apiClient.rotateConfirmBody')}
                onConfirm={() => clientMutation.mutate()}
            />
        );
    } else if (canIssue) {
        issueAction = (
            <Button
                type="primary"
                onClick={() => clientMutation.mutate()}
                disabled={clientMutation.isPending}
            >
                {clientMutation.isPending
                    ? t('console.appDetail.connection.client.creating')
                    : t('console.appDetail.connection.apiClient.createButton')}
            </Button>
        );
    }

    return (
        <SectionCard title={t('console.appDetail.connection.infoCardTitle')}>
            <ValueRow label={t('console.appDetail.connection.issuer')} value={issuer} />
            <ValueRow
                label={t('console.appDetail.connection.audience')}
                value={app.audience || app.code}
            />

            {hasLoginClient && (
                <div data-testid="app-login-client">
                    <h3 className={styles.clientHeading}>
                        {t('console.appDetail.connection.loginClient.title')}
                    </h3>
                    <ValueRow
                        label={t('console.appDetail.connection.clientId')}
                        value={app.kc_client_id || null}
                    />
                    <p className={detail.hint}>
                        {t('console.appDetail.connection.loginClient.publicNote')}
                    </p>
                </div>
            )}

            {hasMachineClient && (
                <div data-testid="app-machine-client">
                    <h3 className={styles.clientHeading}>
                        {t('console.appDetail.connection.apiClient.title')}
                    </h3>
                    <p className={detail.hint}>
                        {t('console.appDetail.connection.apiClient.intro')}
                    </p>
                    <ValueRow
                        label={t('console.appDetail.connection.apiClient.clientId')}
                        value={app.kc_api_client_id || null}
                    />
                    <div className={styles.codeField}>
                        <span className={styles.codeFieldLabel}>
                            {t('console.appDetail.connection.clientSecret')}
                        </span>
                        <span className={styles.codeFieldBox}>
                            <code className={styles.codeFieldValue}>{'•'.repeat(24)}</code>
                            <span className={styles.codeFieldMeta}>
                                {app.kc_api_secret_rotated_at
                                    ? t('console.appDetail.connection.secretRotatedAt', {
                                          date: new Date(
                                              app.kc_api_secret_rotated_at
                                          ).toLocaleDateString('vi-VN'),
                                      })
                                    : t('console.appDetail.connection.secretNeverRotated')}
                            </span>
                        </span>
                    </div>

                    {revealed ? (
                        <AppCredentialReveal
                            fields={revealed.fields}
                            warning={revealed.warning}
                            rotated={revealed.rotated}
                            onCopy={copy}
                            onDismiss={() => setRevealed(null)}
                        />
                    ) : (
                        <>
                            <Callout
                                tone="warning"
                                label={t('console.appDetail.connection.rotateWarningLabel')}
                            >
                                {t('console.appDetail.connection.rotateWarningBody')}
                            </Callout>
                            {issueAction && <div className={detail.buttonRow}>{issueAction}</div>}
                        </>
                    )}
                </div>
            )}
        </SectionCard>
    );
}

export default AppDetailClientsCard;
